/**
 * Server Clock
 * Kiosk device clocks drift by several minutes (automatic time sync is off on the
 * devices), so the display boards keep time from the backend instead.
 *
 * Measures the offset between this device and the server clock (GET /api/time) on
 * first use and every 5 minutes, and exposes serverNow()/serverDate(). Until the
 * first successful measurement it uses the last offset measured on this device
 * (localStorage), or the device clock. A failed, slow or implausible measurement
 * never replaces a good offset, so boards keep working while the server is unreachable.
 */

import { apiConfigReady, buildAssetUrl } from '../config/config.js';

const STORAGE_KEY = 'fids_server_clock_offset';
const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const MAX_ROUND_TRIP_MS = 5000; // the measured offset can be off by up to half the round trip
const MAX_OFFSET_MS = 24 * 60 * 60 * 1000;

const validOffset = (value) => Number.isFinite(value) && Math.abs(value) <= MAX_OFFSET_MS;

const readCachedOffset = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return validOffset(cached?.offsetMs) ? cached.offsetMs : 0;
  } catch (err) {
    return 0;
  }
};

let offsetMs = readCachedOffset();
let syncStarted = false;

/**
 * Measure the offset once.
 * @returns {Promise<boolean>} whether the offset was updated
 */
export async function syncServerClock() {
  const sentAt = Date.now();
  try {
    const response = await fetch(buildAssetUrl('/api/time'), { cache: 'no-store' });
    const receivedAt = Date.now();
    if (!response.ok) return false;

    const { serverTime } = await response.json();
    const roundTripMs = receivedAt - sentAt;
    if (!Number.isFinite(serverTime) || roundTripMs < 0 || roundTripMs > MAX_ROUND_TRIP_MS) return false;

    // Assume the server read its clock halfway through the round trip
    const measured = Math.round(serverTime + roundTripMs / 2 - receivedAt);
    if (!validOffset(measured)) return false;

    offsetMs = measured;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ offsetMs, measuredAt: receivedAt }));
    } catch (err) {
      // Storage unavailable - the in-memory offset still applies
    }
    return true;
  } catch (err) {
    return false;
  }
}

function ensureSyncStarted() {
  if (syncStarted || typeof window === 'undefined') return;
  syncStarted = true;
  apiConfigReady.then(syncServerClock, syncServerClock);
  setInterval(syncServerClock, SYNC_INTERVAL_MS);
}

/** Current time in milliseconds by the server clock (device clock until the first sync) */
export function serverNow() {
  ensureSyncStarted();
  return Date.now() + offsetMs;
}

/** Current time as a Date by the server clock */
export const serverDate = () => new Date(serverNow());

export const getServerClockOffsetMs = () => offsetMs;

// Re-read the cached offset; by default without starting background sync (used by tests)
export function resetServerClock({ autoSync = false } = {}) {
  offsetMs = readCachedOffset();
  syncStarted = !autoSync;
}
